import React, { useState, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import NotificationContext from "../context/NotificationContext";
import { useMutationLock } from "../hooks/useMutationLock";
import PaginationFooter from "./PaginationFooter";
import AppTooltip from "./AppTooltip";
import * as teachersApi from "../api/teachers";

export function TeacherTable({
  data,
  columnKeys,
  handleEdit,
  handleSave,
  onDelete,
  onRefresh,
  onPictureUpdated,
  canEdit = false,
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  loading = false,
}) {
  const [imageViewer, setImageViewer] = useState({ open: false, image: null });
  const notificationCtx = useContext(NotificationContext);
  const { busy, run } = useMutationLock();

  const handleImageView = (image) => {
    setImageViewer({ open: true, image });
  };

  const closeImageViewer = () => {
    setImageViewer({ open: false, image: null });
  };

  const [editStates, setEditStates] = useState({});

  const autoResizeTextarea = (textarea) => {
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useEffect(() => {
    const textareas = document.querySelectorAll(
      "textarea[data-teacher-autoresize]",
    );
    textareas.forEach(autoResizeTextarea);
  }, [data]);

  // Edit logic
  const handleInputChange = (id, key, value) => {
    if (!canEdit) return;
    handleEdit(id, key, value);
    setEditStates((prev) => ({
      ...prev,
      [`${id}-${key}`]: true,
    }));
  };

  const handleSaveClick = async (id, key, value) => {
    if (!canEdit) return;
    await run(async () => {
      await handleSave(id, key, value);
      setEditStates((prev) => ({
        ...prev,
        [`${id}-${key}`]: false,
      }));
    });
  };

  const [file, setFile] = useState({});

  const handleTeacherImageUpdate = async (id) => {
    if (!canEdit) return;
    await run(async () => {
      try {
        if (!file[id]) {
          notificationCtx.show({
            message: "Please upload a picture for the teacher.",
          });
          return;
        }
        const formData = new FormData();
        formData.append("picture", file[id]);
        const response = await teachersApi.updateImage(id, formData);
        const pictureUrl =
          response?.data?.picture ||
          (typeof response?.data === "object" && response?.data?.url) ||
          null;
        if (pictureUrl && onPictureUpdated) {
          onPictureUpdated(id, pictureUrl);
        } else if (onRefresh) {
          await onRefresh();
        }
        setFile((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        notificationCtx.show({
          message: "Teacher picture updated successfully!",
          stat: "success",
        });
      } catch (error) {
        console.error("Error updating teacher picture:", error);
        notificationCtx.show({
          message: "Failed to update teacher picture.",
          stat: "error",
        });
      }
    });
  };

  const handleTeacherImageRemove = async (id) => {
    if (!canEdit) return;
    notificationCtx.show({
      message: "Remove this teacher's picture? This cannot be undone.",
      onConfirm: async () => {
        await run(async () => {
          try {
            await teachersApi.removeImage(id);
            if (onPictureUpdated) {
              onPictureUpdated(id, null);
            } else if (onRefresh) {
              await onRefresh();
            }
            setFile((prev) => {
              const next = { ...prev };
              delete next[id];
              return next;
            });
            if (imageViewer.open) {
              closeImageViewer();
            }
            notificationCtx.show({
              message: "Teacher picture removed.",
              stat: "success",
            });
          } catch (error) {
            console.error("Error removing teacher picture:", error);
            notificationCtx.show({
              message:
                error?.response?.data?.message ||
                "Failed to remove teacher picture.",
              stat: "error",
            });
          }
        });
      },
    });
  };

  return (
    <div
      className="d-flex flex-column"
      style={{
        minHeight: 0,
        height: "100%",
      }}
    >
      {/* ================= TABLE CARD ================= */}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: "#fff",
          borderRadius: "14px",
          border: "1px solid #eeeaf8",
          boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
        }}
      >
        <div
          style={{
            height: "100%",
            overflow: "auto",
          }}
        >
          <table
            style={{
              minWidth: "1100px",
              width: "100%",
              tableLayout: "auto",
              background: "#fff",
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            {/* ================= HEADER ================= */}

            <thead>
              <tr>
                {columnKeys.map((key) => (
                  <th
                    key={key}
                    style={{
                      backgroundColor: "#d5d1fd",
                      color: "#403b55",
                      fontWeight: "600",
                      padding: "14px 16px",
                      textAlign: "center",
                      letterSpacing: "0.4px",
                      fontSize: "13px",
                      borderBottom: "1px solid #e6e3eb",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {key.toUpperCase()}
                  </th>
                ))}

                {canEdit && onDelete && (
                  <th
                    style={{
                      backgroundColor: "#d5d1fd",
                      color: "#403b55",
                      fontWeight: "600",
                      padding: "14px 16px",
                      textAlign: "center",
                      letterSpacing: "0.4px",
                      fontSize: "13px",
                      borderBottom: "1px solid #e6e3eb",
                      whiteSpace: "nowrap",
                      width: "110px",
                    }}
                  >
                    ACTIONS
                  </th>
                )}
              </tr>
            </thead>

            {/* ================= BODY ================= */}

            <tbody>
              {data.map((item) => (
                <tr key={item.id}>
                  {columnKeys.map((key) => (
                    <td
                      key={key}
                      style={{
                        minWidth: key === "id" ? "100px" : "150px",
                        maxWidth: key === "description" ? "300px" : "260px",
                        padding: "13px 16px",
                        verticalAlign: "middle",
                        color: "#403b55",
                        fontSize: "14px",
                        borderBottom: "1px solid #f0eef4",
                      }}
                    >
                      {/* ================= ID ================= */}

                      {key === "id" ? (
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              color: "#403b55",
                              fontWeight: "600",
                              fontSize: "13px",
                              backgroundColor: "#f3f1f7",
                              borderRadius: "7px",
                              padding: "5px 9px",
                            }}
                          >
                            {item[key]}
                          </span>

                          <AppTooltip label="View teacher details">
                          <Link
                            to={`/admin/teachers/${item[key]}`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "28px",
                              height: "28px",
                              borderRadius: "7px",
                              backgroundColor: "#d5d1fd",
                              color: "#403b55",
                              textDecoration: "none",
                              transition: "all 0.2s ease",
                            }}
                          >
                            <i
                              className="fa-solid fa-arrow-up-right-from-square"
                              style={{
                                fontSize: "11px",
                              }}
                            />
                          </Link>
                          </AppTooltip>
                        </div>
                      ) : (
                        <div>
                          {/* ================= PICTURE ================= */}

                          {key.toLowerCase().includes("picture") ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                                minWidth: "180px",
                              }}
                            >
                              {canEdit && (
                                  <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                                  className="form-control"
                                  onChange={(e) =>
                                    setFile({
                                      ...file,
                                      [item.id]: e.target.files[0],
                                    })
                                  }
                                  style={{
                                    border: "1px solid #e5e1ef",
                                    borderRadius: "8px",
                                    padding: "7px 9px",
                                    fontSize: "12px",
                                    backgroundColor: "#fff",
                                  }}
                                />
                              )}

                              <div
                                className="d-flex align-items-center"
                                style={{
                                  gap: "6px",
                                }}
                              >
                                {canEdit && (
                                  <button
                                    onClick={() =>
                                      handleTeacherImageUpdate(item.id)
                                    }
                                    disabled={busy || !file[item.id]}
                                    className="btn"
                                    style={{
                                      backgroundColor: file[item.id]
                                        ? "#bff1db"
                                        : "#f3f1f7",
                                      border: "none",
                                      color: file[item.id]
                                        ? "#355c4a"
                                        : "#aaa5b5",
                                      fontWeight: "600",
                                      fontSize: "12px",
                                      padding: "7px 11px",
                                      borderRadius: "7px",
                                    }}
                                  >
                                    Add New
                                  </button>
                                )}

                                {item[key] ? (
                                  <button
                                    onClick={() => handleImageView(item[key])}
                                    className="btn"
                                    style={{
                                      backgroundColor: "#d5d1fd",
                                      border: "none",
                                      color: "#403b55",
                                      fontWeight: "600",
                                      fontSize: "12px",
                                      padding: "7px 11px",
                                      borderRadius: "7px",
                                    }}
                                  >
                                    View
                                  </button>
                                ) : null}

                                {canEdit && item[key] ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleTeacherImageRemove(item.id)
                                    }
                                    disabled={busy}
                                    className="btn"
                                    style={{
                                      backgroundColor: "#f2c4c4",
                                      border: "none",
                                      color: "#8a3a3a",
                                      fontWeight: "600",
                                      fontSize: "12px",
                                      padding: "7px 11px",
                                      borderRadius: "7px",
                                    }}
                                  >
                                    Remove
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            /* ================= EDITABLE FIELD ================= */

                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "7px",
                                minWidth: "150px",
                              }}
                            >
                              <textarea
                                value={item[key] ?? ""}
                                readOnly={!canEdit}
                                onChange={(e) =>
                                  handleInputChange(
                                    item.id,
                                    key,
                                    e.target.value,
                                  )
                                }
                                onInput={(e) => autoResizeTextarea(e.target)}
                                ref={(node) => autoResizeTextarea(node)}
                                rows={1}
                                data-teacher-autoresize
                                style={{
                                  flex: "1",
                                  minWidth: "0",
                                  minHeight: "32px",
                                  border: "1px solid transparent",
                                  outline: "none",
                                  backgroundColor: "#faf9fc",
                                  borderRadius: "7px",
                                  padding: "7px 9px",
                                  color: "#403b55",
                                  fontSize: "13px",
                                  lineHeight: "1.4",
                                  resize: "none",
                                  overflow: "hidden",
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "break-word",
                                  wordBreak: "normal",
                                  boxSizing: "border-box",
                                  cursor: canEdit ? "text" : "default",
                                }}
                                onFocus={(e) => {
                                  if (!canEdit) return;
                                  e.target.style.border = "1px solid #d5d1fd";
                                  e.target.style.backgroundColor = "#fff";
                                }}
                                onBlur={(e) => {
                                  e.target.style.border =
                                    "1px solid transparent";
                                  e.target.style.backgroundColor = "#faf9fc";
                                }}
                              />

                              {canEdit && (
                                <button
                                  onClick={() =>
                                    handleSaveClick(item.id, key, item[key])
                                  }
                                  disabled={
                                    busy || !editStates[`${item.id}-${key}`]
                                  }
                                  className="btn"
                                  style={{
                                    backgroundColor: editStates[
                                      `${item.id}-${key}`
                                    ]
                                      ? "#bff1db"
                                      : "#f3f1f7",
                                    border: "none",
                                    color: editStates[`${item.id}-${key}`]
                                      ? "#355c4a"
                                      : "#aaa5b5",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    padding: "7px 10px",
                                    borderRadius: "7px",
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                  }}
                                >
                                  Save
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  ))}

                  {/* ================= DELETE ================= */}

                  {canEdit && onDelete && (
                    <td
                      style={{
                        textAlign: "center",
                        padding: "10px 16px",
                        borderBottom: "1px solid #f0eef4",
                      }}
                    >
                      <button
                        className="btn"
                        onClick={() => onDelete(item.id)}
                        style={{
                          backgroundColor: "#f2c4c4",
                          border: "none",
                          borderRadius: "8px",
                          width: "38px",
                          height: "34px",
                          padding: 0,
                        }}
                      >
                        <i
                          className="fa-solid fa-trash"
                          style={{
                            fontSize: "14px",
                            color: "#654343",
                          }}
                        />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= SPACING ================= */}
      <div style={{ height: "10px", flexShrink: 0 }} />

      {/* ================= FOOTER ================= */}

      <PaginationFooter
        page={page}
        limit={limit}
        total={total}
        totalPages={totalPages}
        onPageChange={onPageChange}
        itemLabel="teachers"
        disabled={loading}
      />

      {/* ================= IMAGE VIEWER ================= */}

      {imageViewer.open && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(48,45,69,0.75)",
            zIndex: 10000,
            padding: "20px",
            backdropFilter: "blur(3px)",
          }}
          onClick={closeImageViewer}
        >
          <div
            className="position-relative"
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeImageViewer}
              aria-label="Close"
              className="btn d-flex align-items-center justify-content-center position-absolute"
              style={{
                top: "-14px",
                right: "-14px",
                width: "36px",
                height: "36px",
                padding: 0,
                borderRadius: "50%",
                border: "none",
                backgroundColor: "#fff",
                color: "#403b55",
                zIndex: 10001,
                boxShadow: "0 3px 12px rgba(0,0,0,0.25)",
              }}
            >
              <span
                style={{
                  fontSize: "22px",
                  lineHeight: 1,
                }}
              >
                &times;
              </span>
            </button>

            <img
              src={
                imageViewer.image ||
                "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSIxFqi0YoJt1qdxV5wUgeJjLPA8aDW9N83QA&s"
              }
              alt="Preview"
              className="img-fluid"
              style={{
                display: "block",
                maxWidth: "90vw",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: "12px",
                boxShadow: "0 10px 35px rgba(0,0,0,0.3)",
                backgroundColor: "#fff",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
